import React, { useState, useEffect, useContext, useRef } from 'react';
import { Spinner, Button } from 'reactstrap';
import Select2 from 'react-select2-wrapper';
import CreatableSelect from 'react-select/creatable';
// @ts-ignore
import ReactBSAlert from 'react-bootstrap-sweetalert';
import EmpresaContext from '../../../../contexts/Empresa';
import api from '../../../../services/api';
import DatePickerInput from '../../../../components/DatePickerInput';
// @ts-ignore
import Avatar from '../../../../components/Avatar';
import '../../../marketing/Oportunidades/components/copilotSidebar/styles.css';
import TarefasCalendarModal from './TarefasCalendarModal';

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface Estagio {
  id?: number;
  estagio_id?: number;
  nome: string;
}

interface Funil {
  id: number | string;
  nome: string;
}

interface Tarefa {
  id?: number;
  titulo: string;
  prazo?: string;
  data_conclusao?: string;
  membros?: any[];
  _editando?: boolean;
  _novaMembroIds?: string[];
}

interface CardSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  funilId?: number | string;
  estagios?: Estagio[];
  funis?: Funil[];
  card?: any;
  onSuccess: () => void;
  notify: (type: string, msg: string) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const PRIORIDADE_COR: Record<string, string> = {
  baixa: '#4299e1',
  normal: '#2dce89',
  alta: '#fb6340',
  urgente: '#f5365c',
};

const PRIORIDADE_LABEL: Record<string, string> = {
  baixa: 'Baixa',
  normal: 'Normal',
  alta: 'Alta',
  urgente: 'Urgente',
};

const SITUACAO_COR: Record<string, string> = {
  aberto: '#2dce89',
  concluido: '#5e72e4',
  cancelado: '#f5365c',
  arquivado: '#adb5bd',
};

const SITUACAO_LABEL: Record<string, string> = {
  aberto: 'Aberto',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
  arquivado: 'Arquivado',
};

const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

function formatDateShort(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MESES_CURTOS[d.getMonth()]}`;
}

function formatDateForInput(dateStr?: string) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().substring(0, 16);
}

function getDateBadgeStyle(_dateStr?: string): React.CSSProperties {
  return { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' };
}

// ─── Componente ──────────────────────────────────────────────────────────────

const CardSidebar: React.FC<CardSidebarProps> = ({
  isOpen,
  onClose,
  funilId,
  estagios = [],
  funis,
  card,
  onSuccess,
  notify,
}) => {
  const { empresaSelecionada } = useContext(EmpresaContext);

  // ── Estado do formulário ──
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<React.ReactNode>(null);
  const [colaboradores, setColaboradores] = useState<any[]>([]);
  const [activeFunilId, setActiveFunilId] = useState<string>(funilId ? String(funilId) : '');
  const [activeEstagios, setActiveEstagios] = useState<Estagio[]>(estagios || []);
  const [loadingEstagios, setLoadingEstagios] = useState(false);
  const [tituloFocused, setTituloFocused] = useState(false);
  const [mostrarMembroSelect, setMostrarMembroSelect] = useState(false);
  const [mostrarPrioridadeMenu, setMostrarPrioridadeMenu] = useState(false);
  const [mostrarSituacaoMenu, setMostrarSituacaoMenu] = useState(false);
  const [mostrarCalendarioTarefas, setMostrarCalendarioTarefas] = useState(false);
  const [editandoDataInicio, setEditandoDataInicio] = useState(false);
  const [editandoDataPrazo, setEditandoDataPrazo] = useState(false);

  const parseTags = (raw?: string): string[] =>
    raw ? raw.split(',').map(t => t.trim()).filter(Boolean) : [];

  const [formData, setFormData] = useState(() => ({
    titulo: card?.title || card?.titulo || '',
    descricao: card?.description || card?.descricao || '',
    estagio_id: String(card?.laneId || card?.estagio_id || (estagios?.[0]?.estagio_id || estagios?.[0]?.id) || ''),
    prioridade: card?.prioridade || 'normal',
    data_inicio: formatDateForInput(card?.data_inicio),
    data_prazo: formatDateForInput(card?.data_prazo),
    situacao: card?.situacao || 'aberto',
    membros_ids: card?.membros
      ? card.membros.map((m: any) => String(m.user?.id || m.user_id || m.id || ''))
      : [] as string[],
    tags: parseTags(card?.tags),
  }));

  // ── Membros salvos do card (usados para restringir tarefas) ──
  const membrosDoCard: any[] = card?.membros || [];

  // Lista de usuários selecionados no form em tempo real (para exibir avatares imediatamente)
  const membrosSelecionadosUsers: any[] = formData.membros_ids.map((id: string) => {
    const salvo = membrosDoCard.find((m: any) => String(m.user?.id || m.user_id) === id);
    if (salvo) return salvo.user || salvo;
    const col = colaboradores.find((c: any) => String(c.user_id || c.id) === id);
    return col ? { id: col.user_id || col.id, name: col.name, avatar: col.avatar, avatar_url: col.avatar_url } : null;
  }).filter(Boolean);

  // Criador + membros selecionados sem duplicata — igual ao PipelineKanban
  const todosParticipantes: { key: string; user: any }[] = [
    ...(card?.criador ? [{ key: 'criador', user: card.criador }] : []),
    ...membrosSelecionadosUsers
      .filter((u: any) => u?.id !== card?.criador?.id)
      .map((u: any, i: number) => ({ key: `m-${u?.id || i}`, user: u })),
  ];

  // ── Estado das tarefas ──
  const [tarefas, setTarefas] = useState<Tarefa[]>([]);
  const [loadingTarefas, setLoadingTarefas] = useState(false);
  const [novaTarefaTitulo, setNovaTarefaTitulo] = useState('');
  const novaTarefaRef = useRef<HTMLInputElement>(null);
  const membroSelectRef = useRef<HTMLDivElement>(null);
  const prioridadeMenuRef = useRef<HTMLDivElement>(null);
  const situacaoMenuRef = useRef<HTMLDivElement>(null);

  // ── Efeitos ──

  useEffect(() => {
    if (estagios) setActiveEstagios(estagios);
  }, [estagios]);

  useEffect(() => {
    if (!activeFunilId || !empresaSelecionada) return;
    if (estagios && estagios.length > 0) return;
    let cancelled = false;
    setLoadingEstagios(true);
    api.get(`/common/empresas/${empresaSelecionada.id}/funis/${activeFunilId}/estagios`)
      .then(res => {
        if (cancelled) return;
        const data: Estagio[] = res.data;
        setActiveEstagios(data);
        if (data.length > 0) {
          setFormData(prev => ({
            ...prev,
            estagio_id: prev.estagio_id || String(data[0].estagio_id || data[0].id),
          }));
        }
      })
      .catch(() => { if (!cancelled) notify('danger', 'Falha ao carregar estágios.'); })
      .finally(() => { if (!cancelled) setLoadingEstagios(false); });
    return () => { cancelled = true; };
  }, [activeFunilId]);

  useEffect(() => {
    if (empresaSelecionada) loadColaboradores();
  }, [empresaSelecionada]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (membroSelectRef.current && !membroSelectRef.current.contains(e.target as Node)) {
        setMostrarMembroSelect(false);
      }
      if (prioridadeMenuRef.current && !prioridadeMenuRef.current.contains(e.target as Node)) {
        setMostrarPrioridadeMenu(false);
      }
      if (situacaoMenuRef.current && !situacaoMenuRef.current.contains(e.target as Node)) {
        setMostrarSituacaoMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (card?.id && empresaSelecionada) loadTarefas();
  }, [card?.id, empresaSelecionada]);

  async function loadColaboradores() {
    try {
      const res = await api.get(`/common/empresas/${empresaSelecionada?.id}/colaboradores?situacaoNaEmpresa=A`);
      setColaboradores(res.data);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadTarefas() {
    if (!card?.id) return;
    setLoadingTarefas(true);
    try {
      const res = await api.get(`/common/empresas/${empresaSelecionada?.id}/cards/${card.id}/tarefas`);
      const data = res.data?.rows || res.data || [];
      setTarefas(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    }
    setLoadingTarefas(false);
  }

  // ── Salvar card ──

  const hasPipelineSelector = Array.isArray(funis) && funis.length > 0;
  const resolvedFunilId = activeFunilId || (funilId ? String(funilId) : '');

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!formData.titulo.trim()) {
      notify('warning', 'O título é obrigatório.');
      return;
    }
    setLoading(true);
    const payload = {
      ...formData,
      data_inicio: formData.data_inicio || null,
      data_prazo: formData.data_prazo || null,
      funil_id: resolvedFunilId,
      membros_ids: formData.membros_ids.map(Number),
      tags: formData.tags.join(','),
      ...(card?.entidade_tipo ? { entidade_tipo: card.entidade_tipo, entidade_id: card.entidade_id } : {}),
    };
    try {
      if (card?.id) {
        await api.put(`/common/empresas/${empresaSelecionada?.id}/funis/${resolvedFunilId}/cards/${card.id}`, payload);
        notify('success', 'Card atualizado com sucesso!');
      } else {
        await api.post(`/common/empresas/${empresaSelecionada?.id}/funis/${resolvedFunilId}/cards`, payload);
        notify('success', 'Card criado com sucesso!');
      }
      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      notify('danger', 'Erro ao salvar o card.');
    }
    setLoading(false);
  };

  const confirmDelete = async () => {
    setAlert(null);
    setLoading(true);
    try {
      await api.delete(`/common/empresas/${empresaSelecionada?.id}/funis/${resolvedFunilId}/cards/${card.id}`);
      notify('success', 'Card excluído com sucesso!');
      onSuccess();
      onClose();
    } catch (err) {
      notify('danger', 'Erro ao excluir o card.');
    }
    setLoading(false);
  };

  const handleDelete = () => {
    setAlert(
      <ReactBSAlert
        warning
        closeOnClickOutside={false}
        title="Excluir card?"
        onConfirm={confirmDelete}
        onCancel={() => setAlert(null)}
        showCancel
        confirmBtnBsStyle="danger"
        confirmBtnText="Sim, excluir"
        cancelBtnBsStyle="link"
        cancelBtnText="Cancelar"
        btnSize=""
      >
        Esta ação não pode ser desfeita.
      </ReactBSAlert>
    );
  };

  // ── Tarefas ──

  async function adicionarTarefa() {
    if (!novaTarefaTitulo.trim() || !card?.id) return;
    try {
      const res = await api.post(
        `/common/empresas/${empresaSelecionada?.id}/cards/${card.id}/tarefas`,
        { titulo: novaTarefaTitulo.trim() }
      );
      setTarefas(prev => [...prev, res.data]);
      setNovaTarefaTitulo('');
      if (novaTarefaRef.current) novaTarefaRef.current.focus();
    } catch (e) {
      notify('danger', 'Erro ao adicionar tarefa.');
    }
  }

  async function toggleConcluirTarefa(tarefa: Tarefa) {
    if (!tarefa.id) return;
    const data_conclusao = tarefa.data_conclusao ? null : new Date().toISOString();
    try {
      await api.put(
        `/common/empresas/${empresaSelecionada?.id}/cards/${card.id}/tarefas/${tarefa.id}`,
        { data_conclusao }
      );
      setTarefas(prev =>
        prev.map(t => t.id === tarefa.id ? { ...t, data_conclusao: data_conclusao || undefined } : t)
      );
    } catch (e) {
      notify('danger', 'Erro ao atualizar tarefa.');
    }
  }

  async function excluirTarefa(tarefaId: number) {
    try {
      await api.delete(
        `/common/empresas/${empresaSelecionada?.id}/cards/${card.id}/tarefas/${tarefaId}`
      );
      setTarefas(prev => prev.filter(t => t.id !== tarefaId));
    } catch (e) {
      notify('danger', 'Erro ao excluir tarefa.');
    }
  }

  async function salvarPrazoTarefa(tarefa: Tarefa, prazo: string | null) {
    if (!tarefa.id) return;
    try {
      await api.put(
        `/common/empresas/${empresaSelecionada?.id}/cards/${card.id}/tarefas/${tarefa.id}`,
        { prazo }
      );
      setTarefas(prev =>
        prev.map(t => t.id === tarefa.id ? { ...t, prazo: prazo || undefined } : t)
      );
    } catch (e) {
      notify('danger', 'Erro ao atualizar prazo da tarefa.');
    }
  }

  async function atualizarTituloTarefa(tarefa: Tarefa, titulo: string) {
    if (!tarefa.id || !titulo.trim()) return;
    try {
      await api.put(
        `/common/empresas/${empresaSelecionada?.id}/cards/${card.id}/tarefas/${tarefa.id}`,
        { titulo: titulo.trim() }
      );
      setTarefas(prev =>
        prev.map(t => t.id === tarefa.id ? { ...t, titulo: titulo.trim() } : t)
      );
    } catch (e) {
      notify('danger', 'Erro ao atualizar título da tarefa.');
    }
  }

  async function salvarMembrosTarefa(tarefa: Tarefa, membros_ids: string[]) {
    if (!tarefa.id) return;
    try {
      await api.put(
        `/common/empresas/${empresaSelecionada?.id}/cards/${card.id}/tarefas/${tarefa.id}`,
        { membros_ids: membros_ids.map(Number) }
      );
      setTarefas(prev =>
        prev.map(t => {
          if (t.id !== tarefa.id) return t;
          const membrosAtualizados = membros_ids.map(id => {
            const col = membrosDoCard.find((m: any) => String(m.user?.id || m.user_id) === id);
            return col ? (col.user || col) : { id: Number(id) };
          });
          return { ...t, membros: membrosAtualizados, _editando: false };
        })
      );
    } catch (e) {
      notify('danger', 'Erro ao atualizar membros da tarefa.');
    }
  }

  // ── Render util ──

  const tarefasConcluidas = tarefas.filter(t => !!t.data_conclusao).length;

  if (!isOpen) return null;

  return (
    <>
      {alert}
      <div>
        <div className="copilot-backdrop" onClick={onClose} />

        <div
          className="copilot-drawer"
          style={{ width: '80vw', maxWidth: '1100px' }}
          onClick={e => e.stopPropagation()}
        >
          {/* ── Header ── */}
          <div
            className="copilot-header"
            style={{ alignItems: 'center', justifyContent: 'flex-start', padding: '14px 20px', gap: 12, flexWrap: 'wrap' }}
          >
            {/* Fechar */}
            <button className="copilot-close-btn" onClick={onClose} aria-label="Fechar" style={{ flexShrink: 0 }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {/* Título editável */}
            <input
              value={formData.titulo}
              onChange={e => { const v = e.target.value; setFormData(p => ({ ...p, titulo: v })); }}
              placeholder="Título do card"
              style={{
                flex: 1,
                minWidth: 120,
                fontSize: '1.1rem',
                fontWeight: 700,
                color: '#32325d',
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${tituloFocused ? '#5e72e4' : 'transparent'}`,
                outline: 'none',
                padding: '2px 4px',
                transition: 'border-color 0.15s',
              }}
              onFocus={() => setTituloFocused(true)}
              onBlur={() => setTituloFocused(false)}
            />

            {/* Membros — avatars + botão adicionar */}
            <div ref={membroSelectRef} style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, position: 'relative' }}>
              <div style={{ display: 'flex', flexDirection: 'row-reverse', flexShrink: 0 }}>
                {todosParticipantes.length > 3 && (
                  <div style={{
                    width: 28, height: 28, minWidth: 28,
                    borderRadius: '50%', backgroundColor: '#e9ecef',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#8898aa', fontWeight: 'bold', fontSize: '0.65rem',
                    border: '2px solid #fff', marginRight: -10,
                  }}>
                    +{todosParticipantes.length - 3}
                  </div>
                )}
                {todosParticipantes.slice(0, 3).map(({ key, user }) => (
                  <Avatar
                    key={key}
                    user={user}
                    className="rounded-circle"
                    style={{
                      width: 28, height: 28, minWidth: 28,
                      fontSize: '0.65rem',
                      background: '#5e72e4',
                      marginRight: -10,
                      border: '2px solid #fff',
                      cursor: 'pointer',
                    }}
                    onClick={() => setMostrarMembroSelect(p => !p)}
                  />
                ))}
              </div>
              <button
                onClick={() => setMostrarMembroSelect(p => !p)}
                style={{
                  width: 28, height: 28, borderRadius: '50%',
                  border: '2px dashed #ced4da', background: 'transparent',
                  color: '#8898aa', cursor: 'pointer', fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                title="Gerenciar membros"
              >
                <i className="fas fa-plus" style={{ fontSize: '0.6rem' }} />
              </button>

              {/* Dropdown de membros */}
              {mostrarMembroSelect && (
                <div style={{
                  position: 'absolute', top: 36, right: 0, zIndex: 9999,
                  background: '#fff', border: '1px solid #e9ecef',
                  borderRadius: 8, padding: 12, width: 260,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  maxHeight: 320, overflowY: 'auto',
                }}>
                  <p style={{ fontSize: '0.7rem', fontWeight: 700, color: '#8898aa', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                    Membros do card
                  </p>

                  {/* Selecionados como chips */}
                  {membrosSelecionadosUsers.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                      {membrosSelecionadosUsers.map((u: any) => {
                        const uid = String(u.id || u.user_id);
                        return (
                          <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#f1f5f9', borderRadius: 20, padding: '2px 6px 2px 3px', border: '1px solid #e2e8f0' }}>
                            <Avatar user={u} className="rounded-circle" style={{ width: 18, height: 18, minWidth: 18, fontSize: '0.45rem', background: '#5e72e4' }} />
                            <span style={{ fontSize: '0.73rem', color: '#32325d', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name || u.nome}</span>
                            <button
                              onClick={() => setFormData(p => ({ ...p, membros_ids: p.membros_ids.filter((mid: string) => mid !== uid) }))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#adb5bd', fontSize: '0.8rem', padding: '0 0 0 2px', lineHeight: 1 }}
                            >×</button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Disponíveis para adicionar */}
                  {colaboradores.filter((c: any) => !formData.membros_ids.includes(String(c.user_id || c.id))).length > 0 && (
                    <p style={{ fontSize: '0.68rem', fontWeight: 700, color: '#adb5bd', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Adicionar</p>
                  )}
                  {colaboradores
                    .filter((c: any) => !formData.membros_ids.includes(String(c.user_id || c.id)))
                    .map((c: any) => {
                      const cid = String(c.user_id || c.id);
                      const cUser = { id: cid, name: c.name, avatar: c.avatar, avatar_url: c.avatar_url };
                      return (
                        <div
                          key={cid}
                          onClick={() => setFormData(p => ({ ...p, membros_ids: [...p.membros_ids, cid] }))}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', cursor: 'pointer', borderRadius: 6 }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#f8f9fe'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <Avatar user={cUser} className="rounded-circle" style={{ width: 24, height: 24, minWidth: 24, fontSize: '0.55rem', background: '#5e72e4' }} />
                          <span style={{ fontSize: '0.8rem', color: '#32325d' }}>{c.name}</span>
                        </div>
                      );
                    })
                  }
                  {colaboradores.length > 0 && colaboradores.every((c: any) => formData.membros_ids.includes(String(c.user_id || c.id))) && (
                    <p style={{ fontSize: '0.75rem', color: '#adb5bd', fontStyle: 'italic', textAlign: 'center', padding: '4px 0 0' }}>Todos já adicionados.</p>
                  )}
                </div>
              )}
            </div>

            {/* Prioridade */}
            <div ref={prioridadeMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
              <span
                onClick={() => { setMostrarPrioridadeMenu(p => !p); setMostrarSituacaoMenu(false); }}
                style={{
                  display: 'inline-block',
                  padding: '3px 10px',
                  borderRadius: 20,
                  background: PRIORIDADE_COR[formData.prioridade] + '22',
                  color: PRIORIDADE_COR[formData.prioridade],
                  border: `1px solid ${PRIORIDADE_COR[formData.prioridade]}55`,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                {PRIORIDADE_LABEL[formData.prioridade]}
              </span>
              {mostrarPrioridadeMenu && (
                <div style={{
                  position: 'absolute', top: 28, right: 0, zIndex: 9999,
                  background: '#fff', border: '1px solid #e9ecef',
                  borderRadius: 8, overflow: 'hidden',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 110,
                }}>
                  {Object.entries(PRIORIDADE_LABEL).map(([key, label]) => (
                    <div
                      key={key}
                      onClick={() => { setFormData(p => ({ ...p, prioridade: key })); setMostrarPrioridadeMenu(false); }}
                      style={{
                        padding: '8px 14px', cursor: 'pointer', fontSize: '0.8rem',
                        fontWeight: 600, color: PRIORIDADE_COR[key],
                        background: formData.prioridade === key ? PRIORIDADE_COR[key] + '15' : 'transparent',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = PRIORIDADE_COR[key] + '15')}
                      onMouseLeave={e => (e.currentTarget.style.background = formData.prioridade === key ? PRIORIDADE_COR[key] + '15' : 'transparent')}
                    >
                      {label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Situação */}
            <div ref={situacaoMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
              <span
                onClick={() => { setMostrarSituacaoMenu(p => !p); setMostrarPrioridadeMenu(false); }}
                style={{
                  display: 'inline-block',
                  padding: '3px 10px',
                  borderRadius: 20,
                  background: SITUACAO_COR[formData.situacao] + '22',
                  color: SITUACAO_COR[formData.situacao],
                  border: `1px solid ${SITUACAO_COR[formData.situacao]}55`,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                {SITUACAO_LABEL[formData.situacao]}
              </span>
              {mostrarSituacaoMenu && (
                <div style={{
                  position: 'absolute', top: 28, right: 0, zIndex: 9999,
                  background: '#fff', border: '1px solid #e9ecef',
                  borderRadius: 8, overflow: 'hidden',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 120,
                }}>
                  {Object.entries(SITUACAO_LABEL).map(([key, label]) => (
                    <div
                      key={key}
                      onClick={() => { setFormData(p => ({ ...p, situacao: key })); setMostrarSituacaoMenu(false); }}
                      style={{
                        padding: '8px 14px', cursor: 'pointer', fontSize: '0.8rem',
                        fontWeight: 600, color: SITUACAO_COR[key],
                        background: formData.situacao === key ? SITUACAO_COR[key] + '15' : 'transparent',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = SITUACAO_COR[key] + '15')}
                      onMouseLeave={e => (e.currentTarget.style.background = formData.situacao === key ? SITUACAO_COR[key] + '15' : 'transparent')}
                    >
                      {label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Datas — estilo Trello */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              {/* Data início */}
              <div style={{ position: 'relative' }}>
                {editandoDataInicio ? (
                  <DatePickerInput
                    autoOpen
                    value={formData.data_inicio || null}
                    showTime
                    placeholder="Início"
                    onChange={(iso) => { setFormData(p => ({ ...p, data_inicio: iso || '' })); setEditandoDataInicio(false); }}
                    onClose={() => setEditandoDataInicio(false)}
                  />
                ) : (
                  <span
                    onClick={() => setEditandoDataInicio(true)}
                    title="Clique para editar início"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 8px', borderRadius: 6, cursor: 'pointer',
                      fontSize: '0.73rem', fontWeight: 600,
                      ...(formData.data_inicio
                        ? { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }
                        : { background: '#f1f5f9', color: '#94a3b8', border: '1px dashed #cbd5e1' }),
                    }}
                  >
                    <i className="far fa-calendar-alt" />
                    {formData.data_inicio ? formatDateShort(formData.data_inicio) : 'Início'}
                  </span>
                )}
              </div>

              <span style={{ color: '#cbd5e1', fontSize: '0.7rem' }}>→</span>

              {/* Prazo */}
              <div style={{ position: 'relative' }}>
                {editandoDataPrazo ? (
                  <DatePickerInput
                    autoOpen
                    value={formData.data_prazo || null}
                    showTime
                    placeholder="Prazo"
                    onChange={(iso) => { setFormData(p => ({ ...p, data_prazo: iso || '' })); setEditandoDataPrazo(false); }}
                    onClose={() => setEditandoDataPrazo(false)}
                  />
                ) : (
                  <span
                    onClick={() => setEditandoDataPrazo(true)}
                    title="Clique para editar prazo"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 8px', borderRadius: 6, cursor: 'pointer',
                      fontSize: '0.73rem', fontWeight: 600,
                      ...(formData.data_prazo
                        ? { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }
                        : { background: '#f1f5f9', color: '#94a3b8', border: '1px dashed #cbd5e1' }),
                    }}
                  >
                    <i className="far fa-clock" />
                    {formData.data_prazo ? formatDateShort(formData.data_prazo) : 'Prazo'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="copilot-body" style={{ padding: '20px 24px', gap: 20 }}>

            {/* Pipeline selector */}
            {hasPipelineSelector && (
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Pipeline</label>
                <select
                  className="form-control form-control-sm"
                  value={activeFunilId}
                  onChange={e => {
                    const v = e.target.value;
                    setActiveFunilId(v);
                    setActiveEstagios([]);
                    setFormData(p => ({ ...p, estagio_id: '' }));
                  }}
                >
                  <option value="">Selecione o pipeline...</option>
                  {funis!.map(f => (
                    <option key={f.id} value={String(f.id)}>{f.nome}</option>
                  ))}
                </select>
              </div>
            )}


            {/* Descrição */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Descrição</label>
              <textarea
                className="form-control"
                value={formData.descricao}
                onChange={e => { const v = e.target.value; setFormData(p => ({ ...p, descricao: v })); }}
                placeholder="Descreva a tarefa ou objetivo"
                rows={3}
                style={{ resize: 'vertical' }}
              />
            </div>

            {/* Tags */}
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Tags</label>
              <CreatableSelect
                isMulti
                placeholder="Adicionar tags..."
                value={formData.tags.map(t => ({ value: t, label: t }))}
                onChange={(selected: any) =>
                  setFormData(p => ({
                    ...p,
                    tags: selected ? selected.map((s: any) => s.value as string) : [],
                  }))
                }
                styles={{
                  control: (base: any) => ({ ...base, minHeight: 'calc(2.75rem + 2px)', borderColor: '#DEE2E6' }),
                }}
              />
            </div>

            {/* ── Tarefas ── */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <i className="fas fa-check-square" style={{ color: '#5e72e4' }} />
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#32325d' }}>Tarefas</span>
                <span style={{
                  background: '#e9ecef', color: '#525f7f',
                  borderRadius: 20, padding: '1px 8px', fontSize: '0.75rem', fontWeight: 600,
                }}>
                  {tarefasConcluidas}/{tarefas.length}
                </span>
                {card?.id && tarefas.some(t => t.prazo) && (
                  <button
                    onClick={() => setMostrarCalendarioTarefas(true)}
                    title="Ver tarefas no calendário"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '2px 4px', marginLeft: 2, lineHeight: 1 }}
                  >
                    <i className="far fa-calendar-alt" />
                  </button>
                )}
              </div>

              {!card?.id && (
                <p style={{ fontSize: '0.8rem', color: '#adb5bd', fontStyle: 'italic' }}>
                  Salve o card primeiro para adicionar tarefas.
                </p>
              )}

              {card?.id && (
                <>
                  {loadingTarefas ? (
                    <div style={{ textAlign: 'center', padding: '12px 0' }}>
                      <Spinner size="sm" color="primary" />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                      {tarefas.map(tarefa => (
                        <TarefaItem
                          key={tarefa.id}
                          tarefa={tarefa}
                          membrosDoCard={membrosDoCard}
                          onToggle={() => toggleConcluirTarefa(tarefa)}
                          onExcluir={() => tarefa.id && excluirTarefa(tarefa.id)}
                          onSalvarMembros={(ids) => salvarMembrosTarefa(tarefa, ids)}
                          onSalvarPrazo={(prazo) => salvarPrazoTarefa(tarefa, prazo)}
                        />
                      ))}
                    </div>
                  )}

                  {/* Input nova tarefa */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      ref={novaTarefaRef}
                      value={novaTarefaTitulo}
                      onChange={e => setNovaTarefaTitulo(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') adicionarTarefa(); }}
                      placeholder="Adicionar tarefa..."
                      style={{
                        flex: 1, padding: '7px 10px', borderRadius: 6,
                        border: '1px solid #e9ecef', fontSize: '0.85rem', outline: 'none',
                      }}
                    />
                    <button
                      onClick={adicionarTarefa}
                      disabled={!novaTarefaTitulo.trim()}
                      style={{
                        padding: '7px 14px', borderRadius: 6, border: 'none',
                        background: '#5e72e4', color: '#fff', cursor: 'pointer',
                        fontSize: '0.8rem', fontWeight: 600, opacity: novaTarefaTitulo.trim() ? 1 : 0.5,
                      }}
                    >
                      + Adicionar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Footer ── */}
          <div style={{
            padding: '14px 24px',
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}>
            <div>
              {card?.id && (
                <Button color="danger" outline size="sm" onClick={handleDelete} disabled={loading}>
                  Excluir card
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button color="secondary" size="sm" onClick={onClose} disabled={loading}>
                Cancelar
              </Button>
              <Button color="primary" size="sm" onClick={handleSubmit} disabled={loading}>
                {loading ? <Spinner size="sm" /> : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <TarefasCalendarModal
        isOpen={mostrarCalendarioTarefas}
        onClose={() => setMostrarCalendarioTarefas(false)}
        tarefas={tarefas}
        onToggleConclusao={toggleConcluirTarefa}
        onAtualizarPrazo={salvarPrazoTarefa}
        onAtualizarTitulo={atualizarTituloTarefa}
      />
    </>
  );
};

// ─── Estilo label ─────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.72rem',
  fontWeight: 700,
  color: '#8898aa',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  marginBottom: 6,
};

// ─── Sub-componente: item de tarefa ──────────────────────────────────────────

interface TarefaItemProps {
  tarefa: Tarefa;
  membrosDoCard: any[];
  onToggle: () => void;
  onExcluir: () => void;
  onSalvarMembros: (ids: string[]) => void;
  onSalvarPrazo: (prazo: string | null) => void;
}

const TarefaItem: React.FC<TarefaItemProps> = ({ tarefa, membrosDoCard, onToggle, onExcluir, onSalvarMembros, onSalvarPrazo }) => {
  const [expandido, setExpandido] = useState(false);
  const [editandoPrazo, setEditandoPrazo] = useState(false);
  const [membrosSelecionados, setMembrosSelecionados] = useState<string[]>(
    (tarefa.membros || []).map((m: any) => String(m.user?.id || m.user_id || m.id))
  );

  const concluida = !!tarefa.data_conclusao;

  const membroOptions = membrosDoCard.map((m: any) => ({
    id: String(m.user?.id || m.user_id),
    text: m.user?.name || m.user?.nome || m.name || m.nome || '',
  }));

  return (
    <div style={{
      background: '#f8f9fe',
      border: '1px solid #e9ecef',
      borderRadius: 8,
      padding: '8px 12px',
    }}>
      {/* Linha principal */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={concluida}
          onChange={onToggle}
          style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
        />

        {/* Título */}
        <span style={{
          flex: 1, fontSize: '0.85rem', color: '#32325d',
          textDecoration: concluida ? 'line-through' : 'none',
          opacity: concluida ? 0.6 : 1,
        }}>
          {tarefa.titulo}
        </span>

        {/* Prazo */}
        {editandoPrazo ? (
          <DatePickerInput
            autoOpen
            value={tarefa.prazo || null}
            showTime={false}
            placeholder="dd/mm/aaaa"
            onChange={(iso) => { onSalvarPrazo(iso); setEditandoPrazo(false); }}
            onClose={() => setEditandoPrazo(false)}
            inputClassName=""
            inputStyle={{
              fontSize: '0.7rem', fontWeight: 600,
              padding: '2px 7px', borderRadius: 6, outline: 'none',
              background: '#f1f5f9', color: '#64748b',
              border: '1px solid #cbd5e1', width: 100,
              boxSizing: 'border-box',
            }}
          />
        ) : (
          <span
            onClick={() => setEditandoPrazo(true)}
            title="Clique para definir prazo"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              padding: '2px 6px', borderRadius: 6, cursor: 'pointer',
              fontSize: '0.7rem', fontWeight: 600, flexShrink: 0,
              ...(tarefa.prazo
                ? { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }
                : { color: '#cbd5e1' }),
            }}
          >
            <i className="far fa-calendar-alt" style={{ fontSize: '0.6rem' }} />
            {tarefa.prazo ? formatDateShort(tarefa.prazo) : 'Prazo'}
          </span>
        )}

        {/* Avatars dos membros da tarefa */}
        {(tarefa.membros || []).length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'row-reverse', flexShrink: 0 }}>
            {(tarefa.membros || []).length > 3 && (
              <div style={{
                width: 22, height: 22, minWidth: 22, borderRadius: '50%',
                backgroundColor: '#e9ecef', display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: '#8898aa', fontWeight: 'bold',
                fontSize: '0.5rem', border: '2px solid #fff', marginRight: -8,
              }}>
                +{(tarefa.membros || []).length - 3}
              </div>
            )}
            {(tarefa.membros || []).slice(0, 3).map((m: any, i: number) => (
              <Avatar
                key={m.user?.id || m.user_id || i}
                user={m.user || m}
                className="rounded-circle"
                style={{
                  width: 22, height: 22, minWidth: 22,
                  fontSize: '0.5rem',
                  background: '#5e72e4',
                  marginRight: -8,
                  border: '2px solid #fff',
                }}
              />
            ))}
          </div>
        )}

        {/* Botões */}
        <button
          onClick={() => setExpandido(p => !p)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8898aa', padding: '0 2px' }}
          title="Gerenciar membros"
        >
          <i className="fas fa-user-plus" style={{ fontSize: '0.75rem' }} />
        </button>
        <button
          onClick={onExcluir}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f5365c', padding: '0 2px' }}
          title="Excluir tarefa"
        >
          <i className="fas fa-trash" style={{ fontSize: '0.75rem' }} />
        </button>
      </div>

      {/* Expansão: membros */}
      {expandido && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e9ecef' }}>
          <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#8898aa', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>
            Membros da tarefa
          </p>
          {membroOptions.length === 0 ? (
            <p style={{ fontSize: '0.78rem', color: '#adb5bd', fontStyle: 'italic' }}>
              Adicione membros ao card para atribuir tarefas.
            </p>
          ) : (
            <>
              {/* Selecionados como chips */}
              {membrosSelecionados.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                  {membrosSelecionados.map((mid: string) => {
                    const m = membrosDoCard.find((mb: any) => String(mb.user?.id || mb.user_id) === mid);
                    const u = m ? (m.user || m) : null;
                    if (!u) return null;
                    return (
                      <div key={mid} style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#f1f5f9', borderRadius: 20, padding: '2px 6px 2px 3px', border: '1px solid #e2e8f0' }}>
                        <Avatar user={u} className="rounded-circle" style={{ width: 18, height: 18, minWidth: 18, fontSize: '0.45rem', background: '#5e72e4' }} />
                        <span style={{ fontSize: '0.73rem', color: '#32325d' }}>{u.name || u.nome}</span>
                        <button
                          onClick={() => setMembrosSelecionados((prev: string[]) => prev.filter((id: string) => id !== mid))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#adb5bd', fontSize: '0.8rem', padding: '0 0 0 2px', lineHeight: 1 }}
                        >×</button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Disponíveis */}
              {membroOptions.filter((mo: any) => !membrosSelecionados.includes(mo.id)).map((mo: any) => {
                const m = membrosDoCard.find((mb: any) => String(mb.user?.id || mb.user_id) === mo.id);
                const u = m ? (m.user || m) : { name: mo.text };
                return (
                  <div
                    key={mo.id}
                    onClick={() => setMembrosSelecionados((prev: string[]) => [...prev, mo.id])}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', cursor: 'pointer', borderRadius: 6 }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <Avatar user={u} className="rounded-circle" style={{ width: 22, height: 22, minWidth: 22, fontSize: '0.5rem', background: '#5e72e4' }} />
                    <span style={{ fontSize: '0.8rem', color: '#32325d' }}>{mo.text}</span>
                  </div>
                );
              })}
              {membroOptions.every((mo: any) => membrosSelecionados.includes(mo.id)) && (
                <p style={{ fontSize: '0.75rem', color: '#adb5bd', fontStyle: 'italic' }}>Todos já atribuídos.</p>
              )}

              <button
                onClick={() => { onSalvarMembros(membrosSelecionados); setExpandido(false); }}
                style={{ marginTop: 8, padding: '5px 12px', borderRadius: 6, border: 'none', background: '#5e72e4', color: '#fff', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}
              >
                Salvar membros
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CardSidebar;
